import { useState } from "react";
import { Box, Fade, IconButton, Modal, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { DqsLevel } from "../../../models/dto/cat15/cat15.types";
import { formatNumber } from "../format";

export interface MyDataCertRow {
  year: number;
  verifier: string;
  verifiedOn: string;
  emission: number;
  asOfDate: string;
  dqs: DqsLevel;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Row whose certificate is shown; null when closed. */
  row: MyDataCertRow | null;
  /** Presigned GET url of the cert file (null → empty preview). */
  fileUrl?: string | null;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg)$/i.test(name);
const toDmy = (iso: string) => {
  const [y, m, d] = (iso || "").split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const InfoDot = () => (
  <Box component="img" src="/img/info-circle.svg" alt="" width={12} height={12} flexShrink={0} />
);

/**
 * Borrower View Certificate modal — Figma SME-D-Bank frame 6702:12812. Same
 * two-column shell as the bank's Certificate Information modal (document
 * preview + read-only borrower data) but with no file card or edit actions.
 */
const MyDataCertModal = ({ open, onClose, row, fileUrl = null }: Props) => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const [zoom, setZoom] = useState(1);
  if (!row) return null;

  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  const fields = [
    { label: t("Col-Year"), value: String(row.year) },
    { label: t("Col-Verifier"), value: row.verifier },
    { label: t("Col-Verified-On"), value: toDmy(row.verifiedOn) },
    { label: t("Col-Emission"), value: formatNumber(row.emission) },
    { label: t("Col-As-Of"), value: toDmy(row.asOfDate) },
    { label: t("Col-DQS"), value: `${t("DQS")} ${row.dqs}` },
  ];

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={300}>
        <Box
          position="absolute"
          top="50%"
          left="50%"
          width="1120px"
          maxWidth="96dvw"
          maxHeight="92dvh"
          borderRadius="8px"
          bgcolor="var(--white)"
          sx={{
            transform: "translate(-50%, -50%)",
            outline: "none",
            boxShadow: 24,
            overflowY: "auto",
            overflowX: "hidden",
            "& .MuiTypography-root": { letterSpacing: 0 },
          }}
        >
          {/* Inner flex row sizes to content height so the outer box scrolls both columns as one. */}
          <Box display="flex" alignItems="stretch" width="100%" minHeight="100%">
            {/* LEFT — Document Preview */}
            <Box
              display="flex"
              flexDirection="column"
              gap="20px"
              flex={1}
              minWidth={0}
              p="40px"
              bgcolor="var(--gray8)"
            >
              <Box display="flex" alignItems="center" gap="8px" width="100%">
                <Box width="2px" height="16px" borderRadius="2px" bgcolor="var(--dark)" flexShrink={0} />
                <Typography fontSize="16px" lineHeight="24px" fontWeight={500} color="var(--dark)">
                  {t("Cert-Preview-Title")}
                </Typography>
              </Box>

              <Box display="flex" flex={1} width="100%" minHeight={0}>
                <Box position="relative" flex={1} minWidth={0} bgcolor="var(--doc-preview-surface)" overflow="hidden">
                  {!fileUrl ? (
                    <>
                      <Box
                        position="absolute"
                        sx={{ inset: 0 }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        px="24px"
                      >
                        <Typography fontSize="14px" fontWeight={400} color="var(--gray1)">
                          {t("Cert-Preview-Empty")}
                        </Typography>
                      </Box>
                      <ZoomBar
                        t={t}
                        onOut={zoomOut}
                        onReset={zoomReset}
                        onIn={zoomIn}
                      />
                    </>
                  ) : isImage(fileUrl) ? (
                    <>
                      <Box
                        position="absolute"
                        sx={{ inset: 0, overflow: "auto", display: "flex", justifyContent: "center", alignItems: "flex-start" }}
                      >
                        <Box width="100%" sx={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
                          <Box
                            component="img"
                            src={fileUrl}
                            alt={t("Cert-Preview-Title")}
                            sx={{ display: "block", width: "100%", height: "auto" }}
                          />
                        </Box>
                      </Box>
                      <ZoomBar t={t} onOut={zoomOut} onReset={zoomReset} onIn={zoomIn} />
                    </>
                  ) : (
                    <Box
                      component="iframe"
                      src={`${fileUrl}#toolbar=0&navpanes=0`}
                      title={t("Cert-Preview-Title")}
                      sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* RIGHT — Information */}
            <Box
              display="flex"
              flexDirection="column"
              gap="20px"
              flex={1}
              minWidth={0}
              p="40px"
              bgcolor="var(--white)"
              sx={{ overflowX: "hidden" }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Typography fontSize="20px" lineHeight="24px" fontWeight={700} color="var(--dark)">
                  {t("MyData-Cert-Title")}
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ p: 0 }}>
                  <Box component="img" src="/img/close.svg" alt="" width={24} height={24} />
                </IconButton>
              </Box>

              <Box display="flex" flexDirection="column" gap="20px" width="100%">
                <Box display="flex" alignItems="center" gap="8px">
                  <Typography fontSize="18px" lineHeight="24px" fontWeight={600} color="var(--dark)">
                    {t("Cert-Borrower-Data")}
                  </Typography>
                  <InfoDot />
                </Box>

                {fields.map((f) => (
                  <Box key={f.label} display="flex" flexDirection="column" gap="8px" width="100%">
                    <Box display="flex" alignItems="center" gap="6px">
                      <Typography fontSize="16px" lineHeight="24px" fontWeight={500} color="var(--dark)">
                        {f.label}
                      </Typography>
                      <InfoDot />
                    </Box>
                    <Box
                      px="12px"
                      py="8px"
                      borderRadius="8px"
                      bgcolor="var(--gray5)"
                      border="1px solid var(--gray5)"
                      width="100%"
                    >
                      <Typography fontSize="16px" lineHeight="24px" fontWeight={400} color="var(--dark)">
                        {f.value || "—"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

// Bottom-centered zoom controls shared by the empty / image preview states.
const ZoomBar = ({
  t,
  onOut,
  onReset,
  onIn,
}: {
  t: (k: string) => string;
  onOut: () => void;
  onReset: () => void;
  onIn: () => void;
}) => (
  <Box
    position="absolute"
    bottom={0}
    left={0}
    right={0}
    display="flex"
    gap="16px"
    alignItems="flex-end"
    justifyContent="center"
    py="20px"
  >
    <IconButton size="small" onClick={onOut} sx={{ p: 0 }} aria-label={t("Zoom-Out")}>
      <Box component="img" src="/img/zoom-out.svg" alt="" width={24} height={24} />
    </IconButton>
    <IconButton size="small" onClick={onReset} sx={{ p: 0 }} aria-label={t("Zoom-Reset")}>
      <Box component="img" src="/img/zoom-fit.svg" alt="" width={24} height={24} />
    </IconButton>
    <IconButton size="small" onClick={onIn} sx={{ p: 0 }} aria-label={t("Zoom-In")}>
      <Box component="img" src="/img/zoom-in.svg" alt="" width={24} height={24} />
    </IconButton>
  </Box>
);

export default MyDataCertModal;
